/**
 * Store, types, and validation for RunMetricZscore configuration.
 * This file is separate from RunMetricZscoreSection.tsx to avoid import cycles.
 */
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {type Trigger} from '../TriggersTable/utilTypes';
import {
  getRunFilterConfigFromTriggerFilter,
  isValidRunFilterConfig,
  type RunFilter,
  type RunFilterConfig,
} from './RunFilterShared';
import {type ChangeDirection} from './RunMetricChangeStore';

// Raw z-score filter from API/JSON (matches Go's ZScoreRunMetricFilter)
type RawZScoreFilter = {
  name: string;
  window_size: number;
  threshold: number;
  change_dir: ChangeDirection;
  panelLink: string | undefined;
};

export type ZscoreMetricFilter = {
  metricName: string;
  threshold: string;
  windowSize: string;
  changeDirection: {
    isAboveChecked: boolean;
    isBelowChecked: boolean;
  };
  panelLink: string | undefined;
};

export type RunMetricZscoreConfig = {
  metricFilter: Omit<ZscoreMetricFilter, 'metricName'> & {
    metricName: string | undefined;
  };
  runFilter: RunFilterConfig;
  allowAutoFocus: boolean;
};

const initialRunMetricZscoreConfig: RunMetricZscoreConfig = {
  metricFilter: {
    metricName: undefined,
    threshold: '3.0',
    windowSize: '30',
    changeDirection: {
      isAboveChecked: true,
      isBelowChecked: true,
    },
    panelLink: undefined as string | undefined,
  },
  runFilter: {
    isUsernameFilterEnabled: false,
    username: undefined,
    isRunNameFilterEnabled: false,
    runName: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricZscoreStore = create<RunMetricZscoreConfig>()(
  devtools(
    () => ({
      ...initialRunMetricZscoreConfig,
    }),
    {name: 'RunMetricZscoreStore'}
  )
);

export const RunMetricZscoreActions = {
  initAsync: async (suggestedMetricName?: string, panelLink?: string) => {
    useRunMetricZscoreStore.setState({
      ...initialRunMetricZscoreConfig,
      metricFilter: {
        ...initialRunMetricZscoreConfig.metricFilter,
        metricName: suggestedMetricName,
        panelLink: panelLink,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },

  initFromTrigger: (trigger: Trigger) => {
    const config = getRunMetricZscoreConfig(trigger);
    if (config) {
      useRunMetricZscoreStore.setState(config);
    }
  },
  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setThreshold: (threshold: string) => {
    setMetricFilter({threshold});
  },
  setWindowSize: (windowSize: string) => {
    setMetricFilter({windowSize});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
  setIsAboveChecked: (isAboveChecked: boolean) => {
    setMetricFilter({
      changeDirection: {
        ...useRunMetricZscoreStore.getState().metricFilter.changeDirection,
        isAboveChecked,
      },
    });
  },
  setIsBelowChecked: (isBelowChecked: boolean) => {
    setMetricFilter({
      changeDirection: {
        ...useRunMetricZscoreStore.getState().metricFilter.changeDirection,
        isBelowChecked,
      },
    });
  },
};

export const getRunMetricZscoreConfig = (
  trigger: Trigger
): RunMetricZscoreConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetricZscore
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);

    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      console.error(
        'Failed to parse run filter config from trigger filter:',
        parsedFilter
      );
      return undefined;
    }

    if (
      !parsedFilter.metric_filter &&
      !parsedFilter.run_metric_filter?.zscore_filter
    ) {
      console.error('Failed to parse run metric config:', parsedFilter);
      return undefined;
    }

    const rawZscoreFilter: RawZScoreFilter =
      parsedFilter.run_metric_filter.zscore_filter;

    const result = {
      metricFilter: {
        metricName: rawZscoreFilter.name,
        threshold: rawZscoreFilter.threshold.toString(),
        windowSize: rawZscoreFilter.window_size.toString(),
        changeDirection: getCheckboxesFromChangeDirection(
          rawZscoreFilter.change_dir
        ),
        panelLink: rawZscoreFilter.panelLink,
      },
      runFilter,
      allowAutoFocus: false,
    };
    return result;
  } catch (e) {
    console.error(
      'Failed to parse run metric z-score config from trigger:',
      e,
      trigger
    );
    return undefined;
  }
};

const setMetricFilter = (metricFilter: Partial<ZscoreMetricFilter>) => {
  useRunMetricZscoreStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricZscoreStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

// Validation functions
export const isValidMetricName = (metricName: string | undefined) => {
  return metricName !== undefined && metricName.trim() !== '';
};

export const getNumberValueForField = (field: string | undefined) => {
  const fieldNumber = Number(field || '');
  return fieldNumber;
};

export const isValidThreshold = (threshold: string | undefined) => {
  if (threshold === undefined || threshold === '') {
    return false;
  }
  const thresholdNumber = getNumberValueForField(threshold);
  return (
    !isNaN(thresholdNumber) && isFinite(thresholdNumber) && thresholdNumber > 0
  );
};

export const isValidWindowSize = (windowSize: string | undefined) => {
  if (windowSize === undefined || windowSize === '') {
    return false;
  }
  const windowSizeNumber = getNumberValueForField(windowSize);
  return (
    Number.isInteger(windowSizeNumber) &&
    windowSizeNumber >= 2 &&
    windowSizeNumber <= 100
  );
};

export const isValidChangeDirection = (changeDirection: {
  isAboveChecked: boolean;
  isBelowChecked: boolean;
}) => {
  return changeDirection.isAboveChecked || changeDirection.isBelowChecked;
};

export const getChangeDirectionFromCheckboxes = (
  isAboveChecked: boolean,
  isBelowChecked: boolean
): ChangeDirection => {
  if (isAboveChecked && isBelowChecked) {
    return 'ANY';
  } else if (isAboveChecked) {
    return 'INCREASE';
  } else {
    return 'DECREASE';
  }
};

export const getCheckboxesFromChangeDirection = (
  changeDir: ChangeDirection
): {isAboveChecked: boolean; isBelowChecked: boolean} => {
  return {
    isAboveChecked: changeDir === 'INCREASE' || changeDir === 'ANY',
    isBelowChecked: changeDir === 'DECREASE' || changeDir === 'ANY',
  };
};

export const isValidRunMetricZscoreConfig = (config: RunMetricZscoreConfig) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidThreshold(config.metricFilter.threshold) &&
    isValidWindowSize(config.metricFilter.windowSize) &&
    isValidChangeDirection(config.metricFilter.changeDirection) &&
    isValidRunFilterConfig(config.runFilter)
  );
};

export const getRunMetricZscoreString = (
  metricFilter: RunMetricZscoreConfig['metricFilter'],
  runFilter: RunMetricZscoreConfig['runFilter']
) => {
  const username =
    runFilter.isUsernameFilterEnabled && runFilter.username
      ? `belonging to ${runFilter.username}, `
      : '';

  const runName =
    runFilter.isRunNameFilterEnabled && runFilter.runName?.trim()
      ? `matching "${runFilter.runName}", `
      : '';

  const metricName = metricFilter.metricName || '{Metric}';
  const threshold = metricFilter.threshold || '{Threshold}';

  const changeDir = getChangeDirectionFromCheckboxes(
    metricFilter.changeDirection.isAboveChecked,
    metricFilter.changeDirection.isBelowChecked
  );

  const getDirection = () => {
    switch (changeDir) {
      case 'ANY':
        return `is above ${threshold} or below -${threshold}`;
      case 'INCREASE':
        return `is above ${threshold}`;
      case 'DECREASE':
        return `is below -${threshold}`;
    }
  };

  return `for a run ${username}${runName}the z-score for ${metricName} ${getDirection()}.`;
};

export const getEventConfigForRunMetricZscore = (
  config: RunMetricZscoreConfig
) => {
  const changeDir = getChangeDirectionFromCheckboxes(
    config.metricFilter.changeDirection.isAboveChecked,
    config.metricFilter.changeDirection.isBelowChecked
  );

  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetricZscore,
    filters: {
      filters: [
        {
          zscoreFilter: {
            metricName: config.metricFilter.metricName,
            threshold: config.metricFilter.threshold,
            windowSize: config.metricFilter.windowSize,
            changeDir,
          },
          runFilter: config.runFilter,
        },
      ],
    },
  };
};
